import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Logo } from '@/components/shared/Logo'
import { trackBeginCheckout, trackFunnelPageView } from '@/lib/analytics'

const ADDON_PRICE = 25 // £/extra trainer/month — matches in-app add-on pricing
const ANNUAL_MONTHS = 10 // yearly billing: pay for 10 months, get 12

const PLANS: Record<'starter' | 'pro', { name: string; price: string; priceMonthly: number; features: string[] }> = {
  starter: {
    name: 'Starter',
    price: '£49/month',
    priceMonthly: 49,
    features: ['Unlimited bookings', 'Certificate generation', 'Client portal', 'Email notifications'],
  },
  pro: {
    name: 'Pro',
    price: '£99/month',
    priceMonthly: 99,
    features: ['Everything in Starter', 'QR code verification', 'Priority support', 'Advanced reporting'],
  },
}

// "How did you hear about us?" — the code is what's stored; keep in step with HeardAboutOptions on the API.
const HEARD_ABOUT_OPTIONS: { code: string; label: string }[] = [
  { code: 'search', label: 'Google or another search engine' },
  { code: 'recommended', label: 'Recommended by someone' },
  { code: 'linkedin', label: 'LinkedIn' },
  { code: 'facebook', label: 'Facebook' },
  { code: 'social', label: 'Other social media' },
  { code: 'event', label: 'An industry event' },
  { code: 'advert', label: 'An online advert' },
  { code: 'other', label: 'Somewhere else' },
]

// The exact words shown beside the product-updates box. They're sent with the signup and stored, so there's a record
// of what each person agreed to — change this text and the record changes with it.
const MARKETING_CONSENT_TEXT = 'Yes, email me about new features and product updates. You can unsubscribe at any time.'

/** How this visit started: UTM tags from the link they clicked, and the page they came from (if it wasn't ours). */
function readAttribution(params: URLSearchParams) {
  let referrer: string | null = null
  try {
    if (document.referrer && new URL(document.referrer).origin !== window.location.origin) referrer = document.referrer
  } catch { /* an unparseable referrer is just ignored */ }
  return {
    utmSource: params.get('utm_source'),
    utmMedium: params.get('utm_medium'),
    utmCampaign: params.get('utm_campaign'),
    referrer,
  }
}

export default function SignupPage() {
  const [searchParams] = useSearchParams()
  const [plan, setPlan] = useState<'starter' | 'pro'>(
    (searchParams.get('plan') ?? 'starter') as 'starter' | 'pro'
  )
  const cancelled = searchParams.get('cancelled') === 'true'

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [email, setEmail] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')
  const emailsMatch = email.trim().toLowerCase() === confirmEmail.trim().toLowerCase()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [extraTrainers, setExtraTrainers] = useState(0)
  const [promoCode, setPromoCode] = useState('')
  const [showPromoCode, setShowPromoCode] = useState(false)
  // Yearly is only offered once the API says its Stripe prices exist (see /auth/signup/options).
  const [annualEnabled, setAnnualEnabled] = useState(false)
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly')
  const [heardAbout, setHeardAbout] = useState('')
  const [heardAboutOther, setHeardAboutOther] = useState('')
  // Unticked by default and entirely optional — it must never be pre-selected or bundled with the Terms.
  const [marketingOptIn, setMarketingOptIn] = useState(false)
  // Read once, when the page opens, so it reflects how they arrived rather than anything they did here.
  const [attribution] = useState(() => readAttribution(searchParams))

  const baseSeats = plan === 'pro' ? 3 : 1
  const annual = billing === 'annual'
  const planPrice = (key: 'starter' | 'pro') => PLANS[key].priceMonthly * (annual ? ANNUAL_MONTHS : 1)
  const addonPrice = ADDON_PRICE * (annual ? ANNUAL_MONTHS : 1)
  const priceText = (key: 'starter' | 'pro') => `£${planPrice(key)}/${annual ? 'year' : 'month'}`
  const total = planPrice(plan) + extraTrainers * addonPrice

  const apiBase = import.meta.env.VITE_API_URL ?? ''

  useEffect(() => {
    trackFunnelPageView()
  }, [])

  useEffect(() => {
    api.get<{ annualBillingEnabled: boolean }>('/auth/signup/options')
      .then(res => {
        setAnnualEnabled(res.data.annualBillingEnabled)
        if (res.data.annualBillingEnabled && searchParams.get('interval') === 'annual') setBilling('annual')
      })
      .catch(() => { /* yearly just isn't offered */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If the browser restores this page from cache (e.g. clicking "back" from
  // Stripe checkout), reset the loading state so the form is usable again.
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setLoading(false)
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    if (!emailsMatch) {
      setError("The email addresses don't match. Please check them - this is the address you'll sign in with.")
      return
    }

    const phoneDigits = phone.replace(/\D/g, '').length
    if (phone.trim() && (!/^\+?[0-9 ().-]+$/.test(phone.trim()) || phoneDigits < 7 || phoneDigits > 15)) {
      setError('Please enter a valid phone number, including your country code for international numbers (e.g. +44 20 7946 0000), or leave it blank.')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }

    setLoading(true)
    try {
      const res = await api.post<{ checkoutUrl: string }>('/auth/signup', {
        firstName,
        lastName,
        phone: phone.trim() || null,
        email,
        password,
        companyName,
        plan,
        extraTrainerSeats: extraTrainers,
        // Promo codes are for monthly billing only (a yearly signup with one is refused by the API).
        promoCode: annual ? null : promoCode.trim() || null,
        billingInterval: billing,
        acceptedTerms: agreedToTerms,
        heardAbout: heardAbout || null,
        heardAboutOther: heardAbout === 'other' || heardAbout === 'recommended' ? heardAboutOther.trim() || null : null,
        utmSource: attribution.utmSource,
        utmMedium: attribution.utmMedium,
        utmCampaign: attribution.utmCampaign,
        referrer: attribution.referrer,
        marketingOptIn,
        // Always sent, ticked or not, so the record shows what they were asked.
        marketingConsentWording: MARKETING_CONSENT_TEXT,
      })
      trackBeginCheckout(plan, total)
      window.location.href = res.data.checkoutUrl
    } catch (err: any) {
      setError(err.response?.data?.detail ?? 'Something went wrong. Please try again.')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left panel */}
      <div className="hidden lg:block lg:w-1/2 bg-indigo-700">
       {/* Pinned to the window while the (long) form scrolls, with the content near the top rather than floating in the middle. */}
       <div className="sticky top-0 flex h-screen flex-col overflow-y-auto p-12">
        <Logo size={48} theme="dark" />
        <div className="mt-12">
          <h1 className="text-4xl font-bold text-white mb-4">Start your free trial</h1>
          <p className="text-indigo-200 text-lg">14-day free trial. Cancel any time.</p>
          <div className="mt-8 space-y-3">
            {(Object.entries(PLANS) as ['starter' | 'pro', typeof PLANS['starter']][]).map(([key, info]) => (
              <button
                key={key}
                type="button"
                onClick={() => setPlan(key)}
                className={`w-full text-left rounded-xl p-5 transition-all cursor-pointer border-2 ${
                  plan === key
                    ? 'bg-white/15 border-white/60'
                    : 'bg-white/5 border-transparent hover:bg-white/10'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-white font-semibold text-lg">{info.name}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-indigo-200 text-sm">{priceText(key)}</p>
                    {plan === key && (
                      <span className="inline-block w-4 h-4 rounded-full bg-white flex-shrink-0 flex items-center justify-center">
                        <span className="block w-2 h-2 rounded-full bg-indigo-600" />
                      </span>
                    )}
                  </div>
                </div>
                <ul className="space-y-0.5">
                  {info.features.map(f => (
                    <li key={f} className="text-indigo-200 text-xs flex items-center gap-1.5">
                      <span className="text-indigo-300">✓</span> {f}
                    </li>
                  ))}
                </ul>
              </button>
            ))}
          </div>
        </div>
        <div className="mt-auto pt-8">
          <p className="text-indigo-300 text-sm">© {new Date().getFullYear()} CertSlate</p>
          <p className="text-indigo-400 text-xs mt-1 leading-relaxed">CertSlate is a trading name of DJRS Care Services Ltd. (company number: 17074229)</p>
        </div>
       </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Logo size={44} theme="light" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">Create your account</h2>
          <p className="text-gray-500 mb-8 text-sm">
            Choose package to start free trial
          </p>

          {cancelled && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
              Payment was cancelled — you can try again below.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="plan">Package</Label>
              <select
                id="plan"
                value={plan}
                onChange={e => setPlan(e.target.value as 'starter' | 'pro')}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {(Object.entries(PLANS) as ['starter' | 'pro', typeof PLANS['starter']][]).map(([key, info]) => (
                  <option key={key} value={key}>
                    {info.name} — {priceText(key)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400">{PLANS[plan].name} plan · {priceText(plan)} after 14-day trial</p>
            </div>

            {annualEnabled && (
              <div className="space-y-1.5">
                <Label>Billing</Label>
                <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Billing interval">
                  {([['monthly', 'Monthly', 'Pay each month'], ['annual', 'Yearly', '2 months free']] as const).map(([value, label, hint]) => (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={billing === value}
                      onClick={() => setBilling(value)}
                      className={`rounded-md border px-3 py-2 text-left cursor-pointer transition-colors ${
                        billing === value ? 'border-indigo-500 bg-indigo-50 text-indigo-900' : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span className="block text-sm font-medium">{label}</span>
                      <span className="block text-xs text-gray-500">{hint}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Extra trainer logins</Label>
              <div className="flex items-center gap-3">
                <div className="inline-flex items-center rounded-md border border-input">
                  <button
                    type="button"
                    onClick={() => setExtraTrainers(n => Math.max(0, n - 1))}
                    disabled={extraTrainers === 0}
                    className="px-3 py-1.5 text-lg leading-none text-gray-600 hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Fewer trainers"
                  >−</button>
                  <span className="w-10 text-center text-sm font-medium tabular-nums">{extraTrainers}</span>
                  <button
                    type="button"
                    onClick={() => setExtraTrainers(n => Math.min(50, n + 1))}
                    className="px-3 py-1.5 text-lg leading-none text-gray-600 hover:bg-gray-50 cursor-pointer"
                    aria-label="More trainers"
                  >+</button>
                </div>
                <span className="text-xs text-gray-500">
                  <span className="font-medium text-gray-700">£{addonPrice}</span> per extra trainer / {annual ? 'year' : 'month'}
                </span>
              </div>
              <p className="text-xs text-gray-400">
                Your {PLANS[plan].name} plan includes {baseSeats} trainer login{baseSeats !== 1 ? 's' : ''}. You can change this any time in billing.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  required
                  placeholder="Jane"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  required
                  placeholder="Smith"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone number <span className="font-normal text-gray-400">(optional)</span></Label>
              <Input
                id="phone"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                maxLength={30}
                title="Include your country code for international numbers, e.g. +44 20 7946 0000"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="+44 20 7946 0000"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company">Company name</Label>
              <Input
                id="company"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                required
                placeholder="Acme Training Ltd"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirmEmail">Confirm email</Label>
              <Input
                id="confirmEmail"
                type="email"
                autoComplete="email"
                value={confirmEmail}
                onChange={e => setConfirmEmail(e.target.value)}
                required
                placeholder="Type it again"
                aria-invalid={confirmEmail !== '' && !emailsMatch}
              />
              {confirmEmail !== '' && !emailsMatch && (
                <p className="text-xs text-red-600">The email addresses don't match.</p>
              )}
            </div>
            {annual ? (
              <p className="text-xs text-gray-400">Promo codes can only be used with monthly billing.</p>
            ) : showPromoCode ? (
              <div className="space-y-1.5">
                <Label htmlFor="promoCode">Promo code</Label>
                <Input
                  id="promoCode"
                  value={promoCode}
                  onChange={e => setPromoCode(e.target.value)}
                  autoFocus
                />
                <p className="text-xs text-gray-400">A valid code replaces the free trial with its discount — it won't stack with the 14-day trial.</p>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowPromoCode(true)}
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Have a promo code?
              </button>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                placeholder="At least 8 characters"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm">Confirm password</Label>
              <Input
                id="confirm"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            {/* How did you hear about us? — optional, so it never costs a signup */}
            <div className="space-y-1.5">
              <Label htmlFor="heardAbout">How did you hear about us? <span className="font-normal text-gray-400">(optional)</span></Label>
              <select
                id="heardAbout"
                value={heardAbout}
                onChange={e => setHeardAbout(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">Select…</option>
                {HEARD_ABOUT_OPTIONS.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
              </select>
              {(heardAbout === 'other' || heardAbout === 'recommended') && (
                <Input
                  value={heardAboutOther}
                  maxLength={200}
                  onChange={e => setHeardAboutOther(e.target.value)}
                  placeholder={heardAbout === 'recommended' ? 'Who recommended us? (optional)' : 'Tell us more (optional)'}
                  aria-label={heardAbout === 'recommended' ? 'Who recommended us?' : 'Tell us more'}
                />
              )}
            </div>

            {/* T&C + Privacy Policy checkbox */}
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={e => setAgreedToTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
                required
              />
              <span className="text-sm text-gray-600">
                I agree to the{' '}
                <a
                  href={`${apiBase}/api/v1/legal/terms`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                  onClick={e => e.stopPropagation()}
                >
                  Terms &amp; Conditions
                </a>
                {' '}and{' '}
                <a
                  href={`${apiBase}/api/v1/legal/privacy-policy`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                  onClick={e => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
              </span>
            </label>

            {/* Product-update emails — a separate, optional, unticked box (never bundled with the Terms above) */}
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={e => setMarketingOptIn(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
              />
              <span className="text-sm text-gray-600">{MARKETING_CONSENT_TEXT}</span>
            </label>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
            )}

            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">{annual ? 'Yearly' : 'Monthly'} total after trial</p>
                <p className="text-xs text-gray-400">
                  {PLANS[plan].name} £{planPrice(plan)}
                  {extraTrainers > 0 ? ` + ${extraTrainers} trainer${extraTrainers !== 1 ? 's' : ''} × £${addonPrice}` : ''}
                  {annual ? ' · paid once a year' : ''}
                </p>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                £{total}<span className="text-sm font-normal text-gray-500">{annual ? '/yr' : '/mo'}</span>
              </p>
            </div>

            <Button type="submit" className="w-full" disabled={loading || !agreedToTerms}>
              {loading ? 'Redirecting to payment…' : 'Continue to payment →'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            Already have an account?{' '}
            <a href="/login" className="text-indigo-600 hover:text-indigo-700 font-medium">Sign in</a>
          </p>

          {/* Legal footer */}
          <div className="mt-6 flex items-center justify-center gap-4 text-xs text-gray-400">
            <a href={`${apiBase}/api/v1/legal/privacy-policy`} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">
              Privacy Policy
            </a>
            <span>·</span>
            <a href={`${apiBase}/api/v1/legal/terms`} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">
              Terms &amp; Conditions
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
